import { useEffect, useMemo, useState, useRef } from 'react';
import useStateFromProps from '@/hooks/useStateFromProps';
import { SVGPureIcon } from '@/ui';
import ModelChoiceModal from '@/components/modal/model-choice';
import navbarStore from '@/store/navbar';
import chatStoreManager from '@/store/chat/manager';
import modelChoiceStore from '@/components/model-choice/store';
import { Dropdown } from 'antd';
import client, { SIZE_MODEL_SMALL } from '@/store/client';
import appLogger from '@public/log/monitor';
import wsStore from '@/components/chat-ws/store';
import { observer } from 'mobx-react-lite';
import tti_icon from '@/assets/img/tti_icon.png';

function ModelSelect(props) {
  const {
    hideTitle = false,
  } = props;

  const isWeiShiMinWindow = client.isClient && client.sizeModel == SIZE_MODEL_SMALL;
  const chatStore = isWeiShiMinWindow 
    ? wsStore.currentStore || chatStoreManager.current 
    : chatStoreManager.current;
  const { currAsstId } = chatStore;
  const [popupVisible, setPopupVisible] = useState(false);

  const modelInfo = useMemo(() => {
    return navbarStore.chatModels[currAsstId] || {};
  }, [currAsstId, navbarStore.chatModels]);

  const { pushFrontId } = modelChoiceStore;

  //拼接数字助手icon等信息
  useEffect(() => {
    if (currAsstId) {
      navbarStore.getSpecifyList([currAsstId]);
    }
  }, [currAsstId]);

  useEffect(() => {
    if (!currAsstId && modelChoiceStore.list.length) {
      chatStore.setCurrAsstId(modelChoiceStore.list[0].id);
    }
  }, [modelChoiceStore.list, chatStore.chatId, currAsstId]);

  if (!modelInfo.icon) {
    return <div></div>;
  }

  return (
    <Dropdown
      trigger="click"
      placement="topLeft"
      disabled={!isWeiShiMinWindow}
      open={popupVisible}
      onOpenChange={open => setPopupVisible(open)}
      dropdownRender={() => (
        <DropdownList
          items={modelChoiceStore.list}
          activeKey={modelInfo.id}
          onItemClick={item => {
            setPopupVisible(false);
            chatStore.setCurrAsstId(item.id);

            appLogger.log({
              key : "aibot_plugin",
              action : "click",
              arrt : "aibot_plugin_sidebar_model_list",
              ext : item.title,
              role_id : item.id,
            });
          }}
        />
      )}
    >
      <span
        onClick={evt => {
          if (isWeiShiMinWindow) {
            appLogger.log({
              key : "aibot_plugin",
              action : "click",
              arrt : "aibot_plugin_sidebar_model",
            });
            return;
          }
          ModelChoiceModal.show({
            title: '更多',
            onSucc: model => {
              chatStore.setCurrAsstId(model.id);
              pushFrontId(model.id);
            },
            onClose: () => {},
          });
        }}
        className={[
          'cursor-pointer',
          'select-none',
          'rounded-10px',
          'pl-[8px] pr-[12px] py-[6px]',
          'flex items-center gap-[8px]',
          'bg-normal hover:bg-hover-normal',
          'flex-shrink-0',
        ]}
      >
        <div className={`relative size-24px rounded-8px overflow-hidden ${chatStore && chatStore.isTtiModel ? 'border-1px border-[rgba(0,0,0,0.1)]':''}`}>
          <img
            className="size-24px rounded-8px"
            draggable={false}
            src={modelInfo.icon}
          />
          {chatStore && chatStore.isTtiModel ? (
            <img
              className="size-12px object-cover absolute left-0 bottom-0"
              src={tti_icon}
            />
          ) : null}
        </div>
        {
          !hideTitle
          ? (
            <span className="text-[14px] text-primary font-medium">
              {modelInfo.title}
            </span>
          )
          : null
        }
        <SVGPureIcon className={['w-[12px] h-[8px]']} name="down" />
      </span>
    </Dropdown>
  );
}

function DropdownList(props) {
  const { popupVisible, items = [], onItemClick } = props;
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const [activeKey, setActiveKey] = useStateFromProps({
    value: props.activeKey,
  });

  useEffect(() => {
    if (popupVisible) {
      const { scrollTop: listScrollTop, offsetHeight: listHeight } =
        listRef.current;
      const { offsetTop: itemOffsetTop, offsetHeight: itemHeight } =
        selectedRef.current;

      if (
        itemOffsetTop + itemHeight < listScrollTop ||
        itemOffsetTop > listScrollTop + listHeight
      ) {
        selectedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [popupVisible]);

  return (
    <div
      className={[
        'min-w-[180px] max-h-[321px]',
        'bg-white',
        'rounded-[16px]',
        'py-8px px-2px',
        'flex flex-col',
      ]}
      style={{ boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.1)' }}
    >
      <ul
        ref={listRef}
        className={[
          'overflow-y-scroll nw-scrollbar-thin',
          'pl-[6px] pr-[2px]',
          'flex flex-col gap-[4px] m-0',
        ]}
      >
        {items.map(({ id, title, icon, dl_icon }) => (
          <li
            key={id}
            ref={activeKey === id ? selectedRef : null}
            className={[
              'rounded-[12px]',
              'p-[8px]',
              'flex items-center gap-[8px]',
              'hover:bg-[#F9FAFB]',
              'cursor-pointer',
              {
                '!bg-[#F0F2F5]': activeKey === id,
              },
            ]}
            onClick={() => {
              setActiveKey(id);
              onItemClick?.({ id, title, icon, dl_icon });
            }}
          >
            <img className="size-24px rounded-8px" src={dl_icon || icon} />
            <span className="select-none text-[14px] text-[#222] font-medium">
              {title}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default observer(ModelSelect);
